import { faChevronRight, faEllipsisV } from '@fortawesome/pro-regular-svg-icons'
import { ActionIcon, Menu } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { useRouter } from 'next/router'

import { useAnalytics } from '@flecto/analytics'
import { type ParameterID } from '@flecto/types'
import { Icon } from '@flecto/ui'
import useMediaQuery from '@flecto/ui/lib/utils/useMediaQuery'

import {
  type BookingActionDefinition,
  type BookingActionOption,
  type BookingActionType,
  useBookingActions,
} from '../../hooks/useBookingActions'

export type DropdownBookingActionsProps = {
  bookingId?: ParameterID
  excludeActions?: BookingActionType[]
}

type SubMenuProps = {
  action: BookingActionDefinition
}

function SubMenu({ action }: SubMenuProps) {
  const { isMobile } = useMediaQuery()
  const [opened, { open, close, toggle }] = useDisclosure(false)

  const triggerProps = isMobile
    ? { onClick: toggle, style: { cursor: 'pointer' } }
    : { onMouseEnter: open, onMouseLeave: close }

  return (
    <Menu position='right-start' offset={10} opened={opened} trigger="hover">
      <Menu.Target>
        <Menu.Item
          leftSection={<Icon color={action.color} icon={action.icon} />}
          rightSection={<Icon icon={faChevronRight} />}
          {...triggerProps}
        >
          {action.label}
        </Menu.Item>
      </Menu.Target>
      <Menu.Dropdown mt={0} style={{ zIndex: 1030 }}>
        {action.options?.map((option: BookingActionOption) => (
          <Menu.Item
            key={`option-${option.value}`}
            onClick={(event) => {
              event.stopPropagation()
              action.action(option)
            }}
          >
            {option.label}
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  )
}

export function DropdownBookingActions({
  bookingId,
  excludeActions,
}: DropdownBookingActionsProps) {
  const router = useRouter()
  const track = useAnalytics('segment')
  const { isMobile } = useMediaQuery()

  const {
    bookingActions,
    hasCustomer,
    noHasCustomerNotification,
    bookingStatus,
  } = useBookingActions({
    bookingId,
    excludeActions,
    onSuccessActions: {
      delete_booking: () => router.push('/bookings'),
    },
  })

  if (!bookingActions.length) {
    return null
  }

  return (
    <Menu position='bottom-end'>
      <Menu.Target>
        <ActionIcon variant='default' size={isMobile ? 'sm' : 'md'}>
          <Icon icon={faEllipsisV} />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        {bookingActions.map((action, key) => {
          if (action.useSubMenu && action.options) {
            return <SubMenu key={`booking-action-${key}`} action={action} />
          } else {
            return (
              <Menu.Item
                key={`booking-action-${key}`}
                leftSection={<Icon color={action.color} icon={action.icon} />}
                onClick={() => {
                  if (
                    !hasCustomer &&
                    (action.key === 'reserve' ||
                      action.key === 'pickup' ||
                      action.key === 'change_status_to_quoted' ||
                      action.key === 'create_quote')
                  ) {
                    noHasCustomerNotification()
                  } else if (hasCustomer && action.key === 'create_quote') {
                    track('BOOKING_QUOTE__NEW', {
                      bookingID: bookingId ?? '',
                      bookingStatus: bookingStatus ?? '',
                      clickedOn: 'booking-action-button',
                    })
                    action.action()
                  } else {
                    action.action()
                  }
                }}
              >
                {action.label}
              </Menu.Item>
            )
          }
        })}
      </Menu.Dropdown>
    </Menu>
  )
}
